import { csvToJson } from "../components/csv-to-json";
import { pageNames } from "../views/view-consts";
import { JobCertificateLog } from "./job-certificate";

// this is the production endpoint
const apiEndpoint = "";
// this is for local debugging against the server UAT site
//const apiEndpoint = "https://agwaportal-dev.azurewebsites.net";


const sendMode = "same-origin";


export interface SearchItemBase {
    id: string;
}
export interface SearchItem extends SearchItemBase {

    code: string;
    description: string;
}
export interface SearchItemManufacturer extends SearchItem {
    isResidential: boolean;
    isCommercial: boolean;
    stateIds: number[];
}

export interface SearchItemFabricator extends SearchItem {
    isResidential: boolean;
    isCommercial: boolean;
    stateIds: number[];
}

// it is opted to do a bulk send to the client using caching for all parameter sets 
// as the total amount of data is not that large
export interface SearchParameterLookups {

    manufacturer: SearchItemManufacturer[];
    fabricator: SearchItemFabricator[];
    operatingType: SearchItem[];
    glazingSupplier: SearchItem[];
    glassSpecification: SearchItem[];
    glassType: SearchItem[];
    frameType: SearchItem[];
    frameMaterial: SearchItem[];
    addressState: SearchItem[];
}

export interface SearchParameterFilter {

    manufacturer: SearchItemBase[];
    fabricator: SearchItemBase[];
    operatingType: SearchItemBase[];
    glazingSupplier: SearchItemBase[];
    glassSpecification: SearchItemBase[];
    glassType: SearchItemBase[];
    frameType: SearchItemBase[];
    frameMaterial: SearchItemBase[];
    addressState: SearchItemBase[];
    minHeatingStar: number | null;
    minCoolingStar: number | null;
    minUw: number | null;
    maxUw: number | null;
    shgc: number | null;
    plusSHGCPercentage: number | null;
    minusSHGCPercentage: number | null;
    minTvw: number | null;
    windowId: string | null;
    frameDescription: string | null;
}


export async function fetchCSV(source: string): Promise<any[]> {
    const csv = await fetch(`${apiEndpoint}${source}`, {
        method: "GET",
        mode: sendMode
    });
    return csvToJson(await csv.text(), null) as any[];
}


export interface SearchParameterCache {
    cacheDateTime: Date;
    cacheLifeMinutes: number;
    cache: SearchParameterLookups;
}


export enum ManufacturerFilter {
    residential = 0,
    commercial = 1
}

export interface RangeSelection {
    min: number;
    max: number;
}
export interface SimulationQueryInput {
    //as a query input, this serves as a filter. 
    //an empty list means search all.
    //anything in the list is served as an OR operations
    //there may be some special coding to be done around Frame Types for film and skylights
    lookups?: SearchParameterFilter;
    manufacturerFilter: ManufacturerFilter;
    pageSize?: number;
    pageIndex?: number;
    sortField?: string;
    sortAsc?: boolean;
}

export interface DisplayRow {
    windowId: string;
    frameDescription: string;
    coolingStars: string;
    heatingStars: string;
    heatingPercentage: string;
    coolingPercentage: string;
    uw: number;
    shgc: number;
    tvw: number;
    ai: number;
    manufacturer: string;
    operatingType: string;
    frameType: string;
    frameMaterial: string;
    glassManufacturer: string;
    glassSpecification: string;
    glassType: string;
    glazingDisplayName: string;
    glazingLongDisplayName: string;

}

export interface SimulationQueryResult {
    pageIndex: number;
    recordCountOnServer: number;
    results: DisplayRow[];
}
export interface Favourite {
    displayRow: DisplayRow;
}

export interface JobItem {
    displayRow: DisplayRow;
    width: number | null;
    height: number | null;
    calculation: number | null;
}

let cacheParams: SearchParameterCache | null = null;
const cacheName = "wers-portal-search:searchparams";
const pageStateName = "wers-portal-search:pagestate";
const favouritesName = "wers-portal-search:fav";
const jobItemsName = "wers-portal-search:jobitems";
const basicJobItemsName = "wers-portal-search:basicjobitems";
const certificateDetailsName = "wers-portal-search:cert-details";


function addMinutes(date: Date, minutes) {
    return new Date(date.getTime() + minutes * 60000);
}

export function getCertificateDetails(): JobCertificateLog {
    let certJson = localStorage.getItem(certificateDetailsName);
    if (!certJson) return {
        client: "",
        siteAddress: "",
        locality: "",
        state: "",
        createdBy: "",
        emailAddress: "",
        createdDate: new Date(),
        items: []
    };
    let cert = JSON.parse(certJson) as JobCertificateLog;
    cert.items = [];
    cert.createdDate = new Date();
    return cert;
}

export function setCertificateDetails(cert: JobCertificateLog) {
    let newCert = JSON.parse(JSON.stringify(cert));
    newCert.items = [];
    localStorage.setItem(certificateDetailsName, JSON.stringify(newCert));
}

export function getFavourites(): Favourite[] {
    let fav = localStorage.getItem(favouritesName);
    if (!fav) return [];
    return JSON.parse(fav) as Favourite[];
}
export function setFavourites(favourites: Favourite[]) {
    localStorage.setItem(favouritesName, JSON.stringify(favourites));
}

export function getJobItems(basic?: boolean): JobItem[] {
    let name = basic ? jobItemsName : basicJobItemsName;
    let jobItems = localStorage.getItem(name);
    if (!jobItems) return [];
    return JSON.parse(jobItems) as JobItem[];
}
export function setJobItems(jobItems: JobItem[], basic?: boolean) {
    let name = basic ? jobItemsName : basicJobItemsName;
    localStorage.setItem(name, JSON.stringify(jobItems));

}




export async function getSearchParameters() {
    if (!cacheParams) {
        let localCache: SearchParameterCache | null = null;
        try {
            let store = localStorage.getItem(cacheName);
            if (store) {
                localCache = JSON.parse(store) as SearchParameterCache;
                cacheParams = localCache;
            } else localCache = null;
        } catch {
            localCache = null;
        }
    }

    if (cacheParams) {
        if (cacheParams && addMinutes(new Date(cacheParams.cacheDateTime), cacheParams.cacheLifeMinutes) < new Date())
            cacheParams = null;
    }

    if (!cacheParams) {
        try {
            const response = await fetch(`${apiEndpoint}/api/wers/searchparams`,
                {
                    method: "GET",
                    mode: sendMode

                });
            const lookups = await response.json() as SearchParameterLookups;
            cacheParams = { cache: lookups, cacheDateTime: new Date(), cacheLifeMinutes: 10 };
            localStorage.setItem(cacheName, JSON.stringify(cacheParams));
        } catch (e) {
            console.log(e);
        }
    }
    return cacheParams;
}

export async function runCertificateRequest(job: JobCertificateLog): Promise<void> {
    const response = await fetch(`${apiEndpoint}/api/wers/jobcertificate-basic`,
        {
            method: "POST",
            mode: sendMode,
            body: JSON.stringify(job),
            cache: "no-cache",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
    if (response.ok) {
        return;
    } else {
        console.log(response.status + " " + response.statusText);
        let text = await response.text();
        console.log(text);
        throw new Error(response.status + " " + response.statusText + " " + text);
    }
}

export async function runSimulationSearch(input: SimulationQueryInput): Promise<SimulationQueryResult | null> {
    const response = await fetch(`${apiEndpoint}/api/wers/simulationsearch`,
        {
            method: "POST",
            mode: sendMode,
            body: JSON.stringify(input),
            cache: "no-cache",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
    if (response.ok) {
        return await response.json() as SimulationQueryResult;
    } else {
        console.log(response.status + " " + response.statusText);
        console.log(await response.text());
        return null;
    }

}

export async function runSimulationCSV(input: SimulationQueryInput): Promise<BlobPart | null> {
    const response = await fetch(`${apiEndpoint}/api/wers/simulationsearch-csv`,
        {
            method: "POST",
            mode: sendMode,
            body: JSON.stringify(input),
            cache: "no-cache",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
    if (response.ok) {
        return await response.text();
    } else {
        console.log(response.status + " " + response.statusText);
        console.log(await response.text());
        return null;
    }

}

export interface SimulationSearchPage {
    input: SimulationQueryInput;
    result?: SimulationQueryResult | null;
}

export function saveSimulationQueryInput(input: SimulationQueryInput | null, manufacturerFilter: ManufacturerFilter) {
    if (input)
        localStorage.setItem(`${pageStateName}:${manufacturerFilter}`, JSON.stringify(input));
    else
        localStorage.removeItem(`${pageStateName}:${manufacturerFilter}`);
}
export function getSimulationQueryInput(manufacturerFilter: ManufacturerFilter): SimulationQueryInput | null {
    try {
        let store = localStorage.getItem(`${pageStateName}:${manufacturerFilter}`);
        if (store)
            return JSON.parse(store) as SimulationQueryInput;
        else
            return null;

    } catch (e) {
        console.log(e);
        return null;
    }
}
